import {
  Divider,
  Drawer,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material';
import { useNetwork, usePrevious } from 'ahooks';
import { NAV_ITEMS, SETTINGS_NAV_ITEM, SIDEBAR_WIDTH, TITLE } from 'Constants';
import { POPULAR_BOARDS_IDS } from 'PopularBoards';
import { useCallback, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { RouteParams } from 'Types';
import useReactRouter from 'use-react-router';
import UserAvatar from 'UserAvatar';

import BoardListSection from 'designer/BoardListSection';
import { cloudBoardIDsAtom, localBoardIDsAtom } from 'state/recoilState';
import { isSignInModalOpenAtom, selectUserName } from 'state/signInRecoilState';
import { isDev } from 'utils/utils';

const DRAWER_NAV_ITEMS = [...NAV_ITEMS, SETTINGS_NAV_ITEM];

function AppDrawer(props: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const { isOpen, setIsOpen } = props;

  const userName = useRecoilValue(selectUserName);
  const isOnline = useNetwork().online;
  const signInLabel = isOnline ? userName ?? 'Sign In' : 'Offline';

  const localBoardIDs = useRecoilValue(localBoardIDsAtom);
  const cloudBoardIDs = useRecoilValue(cloudBoardIDsAtom);

  const reactRouter = useReactRouter<RouteParams>();
  const prevRoute = usePrevious(reactRouter.location);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const onAccountClick = useRecoilCallback(
    ({ set }) =>
      () => {
        closeDrawer();
        set(isSignInModalOpenAtom, true);
      },
    [closeDrawer]
  );

  // Close the drawer when the route changes
  useEffect(() => {
    if (reactRouter.location !== prevRoute) {
      closeDrawer();
    }
  }, [reactRouter.location, prevRoute, closeDrawer]);

  return (
    <Drawer anchor="left" open={isOpen} onClose={closeDrawer}>
      <List
        style={{ width: SIDEBAR_WIDTH }}
        subheader={<ListSubheader>{TITLE}</ListSubheader>}
      >
        <Divider />
        {DRAWER_NAV_ITEMS.map(({ label, to, Icon }) => (
          <NavLink
            key={to}
            to={to}
            style={() => ({ color: 'text.primary', textDecoration: 'none' })}
          >
            <ListItemButton>
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <ListItemText primary={label} />
            </ListItemButton>
          </NavLink>
        ))}
        {isDev() && (
          <>
            <Divider />

            <ListItemButton onClick={onAccountClick}>
              <ListItemAvatar>
                <UserAvatar />
              </ListItemAvatar>
              <ListItemText
                primary={<Typography noWrap={true}>{signInLabel}</Typography>}
              />
            </ListItemButton>
          </>
        )}

        <Divider />

        <BoardListSection
          boardIDs={localBoardIDs}
          initialState={false}
          name="My Boards"
        />

        {userName && cloudBoardIDs && (
          <BoardListSection
            boardIDs={cloudBoardIDs}
            initialState={false}
            name={userName}
          />
        )}

        <BoardListSection
          boardIDs={POPULAR_BOARDS_IDS}
          initialState={false}
          name="Popular Boards"
        />
      </List>
    </Drawer>
  );
}

export default AppDrawer;
